import { useQuery } from '@tanstack/react-query';
import { useNostr } from '@nostrify/react';
import type { NostrEvent } from '@nostrify/nostrify';

export interface TrackZap {
  zapper: string;
  amount: number;
  comment?: string;
  event: NostrEvent;
}

export function useTrackZaps(trackKind: number, trackPubkey: string, trackIdentifier: string) {
  const { nostr } = useNostr();

  // Create the 'a' tag coordinate for this addressable event
  const aTag = `${trackKind}:${trackPubkey}:${trackIdentifier}`;

  return useQuery({
    queryKey: ['track-zaps', aTag],
    queryFn: async (c) => {
      const signal = AbortSignal.any([c.signal, AbortSignal.timeout(3000)]);

      // Query zap receipts (kind 9735) that reference this addressable event using 'a' tag
      const zapEvents = await nostr.query(
        [{
          kinds: [9735],
          '#a': [aTag],
          limit: 150,
        }],
        { signal }
      );

      // Parse zap amounts and zappers
      const zaps: TrackZap[] = zapEvents
        .map((event) => {
          // Get bolt11 tag
          const bolt11Tag = event.tags.find(([name]) => name === 'bolt11')?.[1];
          if (!bolt11Tag) return null;

          // Get zapper pubkey from P tag or fall back to event pubkey
          const zapperTag = event.tags.find(([name]) => name === 'P')?.[1];
          const zapper = zapperTag || event.pubkey;

          // Decode amount from bolt11 invoice
          // This is a simplified parser - production apps should use a proper bolt11 decoder
          const amount = parseBolt11Amount(bolt11Tag);

          // Extract comment from zap request (in description tag)
          let comment: string | undefined;
          const descriptionTag = event.tags.find(([name]) => name === 'description')?.[1];
          if (descriptionTag) {
            try {
              const zapRequest = JSON.parse(descriptionTag);
              if (zapRequest.content && zapRequest.content.trim()) {
                // Strip "NodeCast, {Track Title}: " prefix if present
                const rawComment = zapRequest.content.trim();
                const prefixMatch = rawComment.match(/^NodeCast, [^:]+: (.+)$/);
                if (prefixMatch) {
                  // Comment has the prefix format, extract just the user's comment
                  comment = prefixMatch[1];
                } else if (rawComment.startsWith('NodeCast, ') && !rawComment.includes(':')) {
                  // Comment is just "NodeCast, {Track Title}" with no user comment
                  // Don't show anything in the UI
                  comment = undefined;
                } else {
                  // Comment doesn't have our prefix format, show as-is
                  comment = rawComment;
                }
              }
            } catch {
              // Invalid JSON, ignore
            }
          }

          return {
            zapper,
            amount,
            comment,
            event,
          };
        })
        .filter((zap): zap is TrackZap => zap !== null);

      // Sort by amount descending
      zaps.sort((a, b) => b.amount - a.amount);

      return zaps;
    },
    enabled: !!(trackKind && trackPubkey && trackIdentifier),
    // Auto-refetch to pick up new zaps without requiring page refresh
    refetchInterval: 10000, // Refetch every 10 seconds
    refetchOnWindowFocus: true, // Refetch when user returns to tab
    staleTime: 5000, // Consider data stale after 5 seconds
  });
}

/**
 * Simple bolt11 amount parser
 * Extracts the amount in millisats from a bolt11 invoice
 * Format: ln[bc|tb|bcrt][amount][multiplier]1[data]
 */
function parseBolt11Amount(bolt11: string): number {
  try {
    // Lightning invoices format: lnbc[amount][multiplier]1...
    // The "1" separates the human-readable part from the data part
    const match = bolt11.match(/ln(?:bc|tb|bcrt)([0-9]+)([munp]?)1/);
    if (!match) return 0;

    const amount = parseInt(match[1]);
    if (isNaN(amount) || amount <= 0) return 0;

    const multiplier = match[2];

    // Convert to millisats based on multiplier
    let millisats = 0;
    switch (multiplier) {
      case 'm': // milli-bitcoin (0.001 BTC)
        millisats = amount * 100_000_000; // 0.001 BTC = 100,000 sats = 100,000,000 millisats
        break;
      case 'u': // micro-bitcoin (0.000001 BTC)
        millisats = amount * 100_000; // 0.000001 BTC = 100 sats = 100,000 millisats
        break;
      case 'n': // nano-bitcoin (0.000000001 BTC)
        millisats = amount * 100; // 0.000000001 BTC = 0.1 sats = 100 millisats
        break;
      case 'p': // pico-bitcoin (0.000000000001 BTC)
        millisats = amount / 10; // 0.000000000001 BTC = 0.0001 sats = 0.1 millisats
        break;
      default:
        // No multiplier means the amount is in bitcoin
        millisats = amount * 100_000_000_000; // 1 BTC = 100,000,000 sats = 100,000,000,000 millisats
    }

    return Math.floor(millisats);
  } catch {
    return 0;
  }
}

/**
 * Format millisats to a readable string
 */
export function formatSats(millisats: number): string {
  const sats = Math.floor(millisats / 1000);
  if (sats >= 1_000_000) {
    return `${(sats / 1_000_000).toFixed(1)}M`;
  }
  if (sats >= 1_000) {
    return `${(sats / 1_000).toFixed(1)}K`;
  }
  return sats.toString();
}

/**
 * Get total zaps for a track
 */
export function useTrackZapTotal(trackKind: number, trackPubkey: string, trackIdentifier: string) {
  const { data: zaps } = useTrackZaps(trackKind, trackPubkey, trackIdentifier);

  const total = zaps?.reduce((sum, zap) => sum + zap.amount, 0) || 0;
  const count = zaps?.length || 0;

  return { total, count };
}
